import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDownIcon, Pencil } from 'lucide-react'
import type * as React from 'react'

import {
	IconSquare,
	type PastelTone,
} from '#app/components/application-detail/primitives'
import { cn } from '#app/utils/misc'
import { AnimatedButton } from './animated-button'

function Accordion({
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
	return <AccordionPrimitive.Root data-slot="accordion" {...props} />
}

function AccordionItem({
	className,
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
	return (
		<AccordionPrimitive.Item
			data-slot="accordion-item"
			className={cn('border-b last:border-b-0', className)}
			{...props}
		/>
	)
}

function AccordionTrigger({
	className,
	children,
	icon,
	iconTone = 'neutral',
	showEdit = false,
	onEdit,
	isOpen = false,
	headerAction,
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
	/** Optional pastel icon square rendered before the title. */
	icon?: React.ReactNode
	iconTone?: PastelTone
	showEdit?: boolean
	onEdit?: () => void
	isOpen?: boolean
	headerAction?: React.ReactNode
}) {
	return (
		<AccordionPrimitive.Header className="flex">
			<AccordionPrimitive.Trigger
				data-slot="accordion-trigger"
				className={cn(
					'group flex flex-1 cursor-pointer items-center justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none',
					showEdit &&
						'[&[data-state=open]_.edit-btn]:pointer-events-auto [&[data-state=open]_.edit-btn]:opacity-100',
					className,
				)}
				{...props}
			>
				{/* Title (with optional icon square) */}
				<div className="flex items-center gap-3">
					{icon && <IconSquare tone={iconTone}>{icon}</IconSquare>}
					{children}
				</div>

				{/* Right section */}
				<div className="flex items-center gap-3">
					{headerAction && isOpen ? (
						<div className="header-action pointer-events-auto opacity-100">
							{headerAction}
						</div>
					) : null}
					{showEdit && isOpen && (
						<AnimatedButton
							type="button"
							onClick={(e) => {
								e.stopPropagation()
								onEdit?.()
							}}
							className="rounded-[30px] bg-[#32302F] px-4 py-2 text-[14px] font-medium text-white transition-colors duration-200 hover:bg-[484848]"
						>
							<Pencil /> Edit
						</AnimatedButton>
					)}

					<ChevronDownIcon className="size-4 shrink-0 text-[#121212] transition-transform duration-200 group-data-[state=open]:rotate-180" />
				</div>
			</AccordionPrimitive.Trigger>
		</AccordionPrimitive.Header>
	)
}

function AccordionContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
	return (
		<AccordionPrimitive.Content
			data-slot="accordion-content"
			className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
			{...props}
		>
			<div className={cn('pt-0 pb-4', className)}>{children}</div>
		</AccordionPrimitive.Content>
	)
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
